import React from "react";
import {
  Box,
  Button,
  ButtonGroup,
  Center,
  Flex,
  HStack,
  Heading,
  Text,
  useColorMode,
} from "@chakra-ui/react";
import { Chart } from "react-google-charts";
import TradingSpinner from "../TradingSpinner";

const TIME_FRAMES = ["Day", "Week", "Month", "Year"];

const AccountValueChartCard = ({
  accountValues,
  preferences,
  featureFlag,
  filter_av_time,
  onFilterAvTimeChange,
  avtimeloading,
  format,
  setDateFormat,
  makeAreaOptions,
  onOptInClick,
  // TRADE-956 / TRADE-959: surface drawdown next to High/Low/Current.
  accountValueMetrics,
}) => {
  const { colorMode } = useColorMode();
  const cardBg = colorMode === "light" ? "white" : "gray.700";
  const headingColor = colorMode === "light" ? "gray.600" : "gray.200";
  const currentColor = colorMode === "light" ? "gray.600" : "gray.300";

  const values = accountValues?.accountvalues || [];

  const chartData = [
    ["Date", "Value"],
    ...values.map(({ date, accountvalue }) => [
      new Date(date).toLocaleDateString(
        "en-US",
        featureFlag
          ? setDateFormat()
          : { timeZone: "UTC", month: "2-digit", day: "2-digit" }
      ),
      accountvalue,
    ]),
  ];

  return (
    <Box bg={cardBg} rounded="lg" p={4} boxShadow="sm">
      {featureFlag ? (
        <>
          <Flex
            justifyContent="space-between"
            alignItems="center"
            mb={1}
            flexWrap="wrap"
            gap={2}
          >
            <Box>
              <Heading
                className="page-heading"
                fontSize="sm"
                color={headingColor}
              >
                Account Value
              </Heading>
              {values.length > 0 && (
                <HStack spacing={4} mt={1}>
                  <Text
                    fontSize="xs"
                    color="green.500"
                    fontWeight="semibold"
                  >
                    High:{" "}
                    {format(
                      Math.max(...values.map((av) => av.accountvalue)),
                      preferences.preferred_currency
                    )}
                  </Text>
                  <Text fontSize="xs" color="red.400" fontWeight="semibold">
                    Low:{" "}
                    {format(
                      Math.min(...values.map((av) => av.accountvalue)),
                      preferences.preferred_currency
                    )}
                  </Text>
                  <Text
                    fontSize="xs"
                    fontWeight="bold"
                    color={currentColor}
                  >
                    Current:{" "}
                    {format(
                      values[values.length - 1].accountvalue,
                      preferences.preferred_currency
                    )}
                  </Text>
                  {accountValueMetrics && accountValueMetrics.max_drawdown_pct != null && (
                    <Text fontSize="xs" color="red.400" fontWeight="semibold">
                      Max DD: {accountValueMetrics.max_drawdown_pct}%
                    </Text>
                  )}
                  {accountValueMetrics && accountValueMetrics.equity_high_water != null && (
                    <Text fontSize="xs" color={currentColor} fontWeight="semibold">
                      High Water:{" "}
                      {format(
                        accountValueMetrics.equity_high_water,
                        preferences.preferred_currency
                      )}
                    </Text>
                  )}
                </HStack>
              )}
            </Box>
            <ButtonGroup size="xs" isAttached variant="outline">
              {TIME_FRAMES.map((t) => {
                const isActive =
                  filter_av_time === t || (!filter_av_time && t === "Day");
                return (
                  <Button
                    key={t}
                    colorScheme="gray"
                    variant={isActive ? "solid" : "ghost"}
                    onClick={() => onFilterAvTimeChange(t)}
                  >
                    {t}
                  </Button>
                );
              })}
            </ButtonGroup>
          </Flex>
          <Box h="450px">
            {!avtimeloading ? (
              <Chart
                chartType="AreaChart"
                width="100%"
                height="450px"
                data={chartData}
                options={makeAreaOptions()}
              />
            ) : (
              <Center h="100%">
                <TradingSpinner size="40px" />
              </Center>
            )}
          </Box>
        </>
      ) : (
        <Box position="relative" minH="450px">
          <Heading
            className="page-heading"
            fontSize="sm"
            color={headingColor}
            mb={2}
          >
            Account Value
          </Heading>
          <Box h="450px">
            <Chart
              chartType="AreaChart"
              width="100%"
              height="450px"
              data={chartData}
              options={makeAreaOptions()}
            />
          </Box>
          <Box
            position="absolute"
            top={0}
            left={0}
            w="100%"
            h="100%"
            bg={
              colorMode === "light"
                ? "rgba(255, 255, 255, 0.85)"
                : "rgba(26, 32, 44, 0.9)"
            }
          />
          <Box
            position="absolute"
            top="50%"
            left="50%"
            transform="translate(-50%, -50%)"
            textAlign="center"
            p="10px 20px"
          >
            <Text
              fontFamily="'Open Sans', sans-serif"
              color={colorMode === "light" ? "gray.600" : "gray.200"}
              mb={4}
            >
              Track Your Progress: Start Tracking Your Account Value
            </Text>
            <Button onClick={onOptInClick} borderRadius="md">
              Enter Account Value
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default AccountValueChartCard;
